import { Input } from "@/components/podkit/forms/Input";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { ChangeEvent, FC, KeyboardEvent } from "react";
import { useCallback, useEffect, useState } from "react";

// Docker image regex for validation
// Source: https://docs.docker.com/reference/cli/docker/image/tag/#extended-description
const dockerImageRegex =
    /^(?:(?:(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])(?:\.(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]))*(?::[0-9]+)?\/)?[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?(?:\/[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?)*(?::[a-zA-Z0-9_.-]{1,128})?(?:@[a-z][a-z0-9]*(?:[.-][a-z][a-z0-9]*)*:[a-f0-9]{64})?|[a-z0-9]+(?:[._][a-z0-9]+)*)$/i;

type DefaultImageProps = {
    editable?: boolean;
};

export const DefaultImage: FC<DefaultImageProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [value, setValue] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (organizationPolicies) {
            setValue(organizationPolicies.defaultEnvironmentImage || "");
            setIsDirty(false);
            setError(null);
        }
    }, [organizationPolicies]);

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const handleValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        setIsDirty(true);
        setError(null);
    }, []);

    const validateImage = useCallback((imageValue: string): boolean => {
        if (!imageValue.trim()) {
            // it is ok to have an empty image name
            return true;
        }

        if (!dockerImageRegex.test(imageValue)) {
            setError("Expected format: [HOST[:PORT]/]NAMESPACE/REPOSITORY[:TAG]");
            return false;
        }

        setError(null);
        return true;
    }, []);

    const handleSave = useCallback(async () => {
        if (!isDirty) return;

        if (!validateImage(value)) {
            return;
        }

        const params: PartialMessage<OrganizationPolicies> = {
            defaultEnvironmentImage: value,
        };

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            setIsDirty(false);
            toast({
                title: "Default environment image updated",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [value, updateOrganizationPolicies, isDirty, toast, validateImage]);

    // Handle save on blur
    const handleBlur = useCallback(() => {
        if (isDirty) {
            void handleSave();
        }
    }, [isDirty, handleSave]);

    // Handle Enter key press
    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && isDirty) {
                void handleSave();
            }
        },
        [isDirty, handleSave],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex flex-col gap-0.5">
                    <Heading3 className="text-base">Default environment image</Heading3>
                    <Text className="text-sm text-content-secondary">
                        Default devcontainer image used when no configuration is present in the repository. Leave empty to use the Ona system default.{" "}
                        <ExternalLink
                            href="https://ona.com/docs/ona/organizations/policies#default-environment-image"
                            iconSize="sm"
                        >
                            Learn more
                        </ExternalLink>
                    </Text>
                </div>
                <Input
                    data-testid="default-environment-image-input"
                    type="text"
                    value={value}
                    onChange={handleValueChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className={cn("max-w-none", error && "border-border-error")}
                    placeholder="mcr.microsoft.com/devcontainers/base:ubuntu-24.04"
                    disabled={!editable}
                />
                <ErrorMessage error={error} className="text-sm" />
            </SkeletonBlock>
        </div>
    );
};
